import {REHYDRATE} from 'redux-persist';

import {unexpectedCase} from 'nutshell-core/reducer-utils';

import type {SalesDocumentType} from 'shells/lead-sidebar/sales-documents/types';

import {ActionTypes} from './email-composer-actions';
import type {EmailComposerAction} from './email-composer-actions';
import {CREATE_FROM_SCRATCH_ID} from './sender/types';

export type EmailComposerState = {
    isOpen: boolean;
    wasMinimizedForEntityId: string | null | undefined;
    seededWithEmailAddress: string | null | undefined;
    seedWithAllPrimaryRelatedEmailAddresses: boolean | null | undefined;
    selectedSequenceId: string;
    selectedTemplateId: string;
    isPromptingOverride: boolean;
    pendingEmailAddress: string | null | undefined;
    activeEntityId: string | null | undefined;
    showEntityLink: boolean | null | undefined;
    originSalesDocumentType: SalesDocumentType | null | undefined;
};

export const defaultEmailComposerState = {
    wasMinimizedForEntityId: null,
    isOpen: false,
    selectedSequenceId: CREATE_FROM_SCRATCH_ID,
    selectedTemplateId: CREATE_FROM_SCRATCH_ID,
    seededWithEmailAddress: null,
    seedWithAllPrimaryRelatedEmailAddresses: false,
    // Are we currently asking the user if they'd like
    // to discard their current email?
    isPromptingOverride: false,
    pendingEmailAddress: null,
    activeEntityId: null,
    showEntityLink: false,
    originSalesDocumentType: null,
} as const;

export const reducer = (
    state: EmailComposerState | null | undefined = defaultEmailComposerState,
    action: EmailComposerAction
): EmailComposerState => {
    switch (action.type) {
        case ActionTypes.EMAIL_COMPOSER_MINIMIZED:
            // @ts-expect-error
            return {
                ...state,
                isOpen: false,
                wasMinimizedForEntityId: action.payload.entityId,
            };
        case ActionTypes.EMAIL_COMPOSER_OPENED_WITH_ID:
            // @ts-expect-error
            return {
                ...state,
                isOpen: true,
                wasMinimizedForEntityId: null,
                isPromptingOverride: false,
                activeEntityId: action.payload.entityId,
                showEntityLink: action.payload.showEntityLink,
                originSalesDocumentType: action.payload.originSalesDocumentType,
                seededWithEmailAddress: action.payload.seededWithEmailAddress,
                seedWithAllPrimaryRelatedEmailAddresses:
                    action.payload.seedWithAllPrimaryRelatedEmailAddresses,
            };
        case ActionTypes.EMAIL_COMPOSER_CLOSED:
            return defaultEmailComposerState;
        case ActionTypes.EMAIL_COMPOSER_SEQUENCE_SELECTED:
            // @ts-expect-error
            return {
                ...state,
                selectedSequenceId: action.payload,
            };
        case ActionTypes.EMAIL_COMPOSER_TEMPLATE_SELECTED:
            // @ts-expect-error
            return {
                ...state,
                selectedTemplateId: action.payload,
            };
        case ActionTypes.EMAIL_COMPOSER_ENLARGED:
            // @ts-expect-error
            return {
                ...state,
                isOpen: true,
                // Can't set wasMinimizedForEntityId to null, will blow away email form values if we do
            };
        case ActionTypes.EMAIL_COMPOSER_OPENED_WITH_ADDRESS:
            // @ts-expect-error
            return {
                ...state,
                isOpen: true,
                wasMinimizedForEntityId: null,
                isPromptingOverride: false,
                seededWithEmailAddress: action.payload.emailAddress,
                activeEntityId: action.payload.entityId,
            };
        case ActionTypes.EMAIL_COMPOSER_OVERRIDE_PROMPTED:
            // @ts-expect-error
            return {
                ...state,
                isPromptingOverride: true,
                activeEntityId: action.payload.entityId,
                pendingEmailAddress: action.payload.emailAddress ?? null,
            };
        case ActionTypes.EMAIL_COMPOSER_OVERRIDE_CANCELLED:
            // @ts-expect-error
            return {
                ...state,
                isPromptingOverride: false,
                pendingEmailAddress: null,
            };
        case ActionTypes.EMAIL_COMPOSER_OPENED_WITH_NEW_EMAIL:
            // @ts-expect-error
            return {
                ...state,
                isOpen: true,
                wasMinimizedForEntityId: null,
                isPromptingOverride: false,
            };
        // @ts-expect-error
        case REHYDRATE: {
            // @ts-expect-error
            const persistedState = action.payload;
            if (!persistedState || !persistedState.ui || !persistedState.ui.emailComposer) {
                // @ts-expect-error
                return state;
            }

            // If we aren't minimized, don't try to rehydrate any state
            if (!persistedState.ui.emailComposer.wasMinimizedForEntityId) {
                // @ts-expect-error
                return state;
            }

            return persistedState.ui.emailComposer;
        }
        default:
            // @ts-expect-error
            unexpectedCase(action.type);

            // @ts-expect-error
            return state;
    }
};
